/**
 * Clé d'idempotence d'une clôture, gardée sur l'appareil (WEB1-04).
 *
 * Elle rend l'envoi de « Terminer l’intervention » sûr à rejouer : réponse perdue,
 * clôture mise en file puis rejouée, nouvel appui. Elle est liée à la
 * réouverture : `fa.closure.<intervention>.<reopened_count>`. Après une
 * réouverture par le bureau, la nouvelle clôture a donc une nouvelle clé ; avec
 * l'ancienne, le serveur rejouerait la réponse de la première clôture sans rien
 * clôturer. La clé est effacée dès que la clôture est confirmée, en ligne ou
 * par la file. Stockage indisponible (navigation privée, quota) : la clé vit en
 * mémoire pour la session, jamais d'exception à l'appui.
 */
const PREFIX = 'fa.closure.';
const memory = new Map<string, string>();

function keyName(interventionId: string, reopenedCount: number): string {
  return `${PREFIX}${interventionId}.${reopenedCount}`;
}

function read(name: string): string | null {
  try {
    return localStorage.getItem(name) ?? memory.get(name) ?? null;
  } catch {
    return memory.get(name) ?? null;
  }
}

function write(name: string, value: string): void {
  memory.set(name, value);
  try {
    localStorage.setItem(name, value);
  } catch {
    // Stockage refusé : la copie en mémoire suffit pour cette session.
  }
}

function storedNames(interventionId: string): string[] {
  const legacy = `${PREFIX}${interventionId}`;
  const names = new Set([...memory.keys()].filter((name) => name === legacy || name.startsWith(`${legacy}.`)));
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const name = localStorage.key(index);
      if (name && (name === legacy || name.startsWith(`${legacy}.`))) names.add(name);
    }
  } catch {
    // Stockage illisible : seules les clés en mémoire sont connues.
  }
  return [...names];
}

/**
 * La clé de la clôture en cours, créée au premier appui. Une clé d'avant ce
 * changement (`fa.closure.<id>`, sans révision) n'est reprise que si
 * l'intervention n'a jamais été rouverte : sinon elle désigne une clôture déjà
 * faite et serait rejouée sans effet.
 */
export function closureKeyFor(interventionId: string, reopenedCount: number): string {
  const name = keyName(interventionId, reopenedCount);
  const existing = read(name);
  if (existing) return existing;
  const legacy = reopenedCount === 0 ? read(`${PREFIX}${interventionId}`) : null;
  const key = legacy ?? crypto.randomUUID();
  write(name, key);
  return key;
}

/** Clôture confirmée (en ligne ou par la file) : les clés de cette intervention s'effacent. */
export function clearClosureKeys(interventionId: string): void {
  for (const name of storedNames(interventionId)) {
    memory.delete(name);
    try {
      localStorage.removeItem(name);
    } catch {
      // Déjà inaccessible : rien à effacer.
    }
  }
}
